import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Activity,
  AlertTriangle,
  Building2,
  CheckCircle2,
  Copy,
  CreditCard,
  Database,
  Eye,
  FileText,
  Loader2,
  Plug,
  RefreshCw,
  Settings,
  Shield,
  StopCircle,
  Trash2,
  UserPlus,
  Users,
  Wifi,
  WifiOff
} from 'lucide-react'
import { apiService } from '../../services/api'
import { toastError } from '../../services/errorUtils'

type TabId = 'overview' | 'organizations' | 'users' | 'connections' | 'billing' | 'impersonation' | 'audit' | 'platform'

interface Organization {
  id: string
  name: string
  slug: string
  status: string
  billing_email?: string | null
  timezone?: string
}

interface RoleDefinition {
  id: string
  name: string
  description: string
  permissions: string[]
}

interface Membership {
  id: string
  organization_id: string
  user_id: string
  role: string
  status: string
}

interface AdminUser {
  id: string
  name: string
  email: string
  status: string
  mfa_enabled: boolean
  last_login_at?: string | null
  membership: Membership
}

interface ProviderConnection {
  id: string
  provider: string
  name: string
  status: string
  health: string
  capabilities: string[]
  config: Record<string, string | number | boolean | null>
  secret_status: string
  last_checked_at?: string | null
}

interface Plan {
  id: string
  name: string
  price_cents: number | null
  currency: string
  interval: string
  limits: Record<string, number | null>
  overage: Record<string, number | null>
  abacatepay_product_id?: string | null
}

interface Subscription {
  id: string
  provider: string
  plan_id: string
  status: string
  external_id?: string | null
  checkout_url?: string | null
  current_period_start?: string
  current_period_end?: string
  plan?: Plan
}

interface UsageEvent {
  id: string
  metric: string
  quantity: number
  unit: string
  source: string
  created_at: string
}

interface UsageSummary {
  totals: Record<string, number>
  events: UsageEvent[]
}

interface ProviderDraft {
  config: Record<string, string>
  secret: string
  live: boolean
}

interface ProviderCheck {
  status: 'pass' | 'warn' | 'fail' | 'skip'
  code: string
  message: string
  detail?: unknown
}

interface ProviderTestResult {
  ok: boolean
  provider: string
  health: string
  checks: ProviderCheck[]
  recommendations: string[]
  tested_at: string
}

interface ImpersonationSession {
  id: string
  status: string
  reason: string
  started_at: string
  expires_at: string
  restrictions: string[]
  actor?: Pick<AdminUser, 'id' | 'name' | 'email'>
  target?: Pick<AdminUser, 'id' | 'name' | 'email'>
}

interface AdminBillingSubscription {
  id: string
  organization_id: string
  organization_name?: string
  plan_code: string
  status: string
  current_period_start?: string | null
  current_period_end?: string | null
  period_days?: number | null
  external_subscription_id?: string | null
  checkout_url?: string | null
  activated_at?: string | null
  deactivated_at?: string | null
  created_at: string
}

interface AdminBillingOrgDetail {
  organization: Organization
  subscription: Record<string, unknown> | null
  manual_logs: Record<string, unknown>[]
  blocked_logs: Record<string, unknown>[]
  members: Record<string, unknown>[]
}

interface Invitation {
  id: string
  organization_id: string
  invited_by: string
  email: string
  role: string
  token: string
  status: string
  expires_at: string
  created_at: string
  accepted_at: string | null
}

interface AuditLog {
  id: string
  actor_user_id: string
  target_user_id?: string | null
  action: string
  resource_type: string
  resource_id?: string | null
  created_at: string
  metadata?: Record<string, unknown>
}

interface CurrentUser {
  id?: string
  name?: string
  email?: string
  is_platform_admin?: boolean
}

interface AdminSummary {
  organization?: Organization
  users: {
    total: number
    active: number
    invited: number
  }
  providers: {
    total: number
    ready: number
    planned: number
    unhealthy: number
  }
  billing: {
    subscription?: Subscription
    plan?: Plan
    usage: Record<string, number>
  }
  developer: {
    api_keys: number
    webhooks: number
    mcp_clients: number
  }
  security: {
    active_impersonations: number
    audit_events: number
  }
  recent_audit_logs: AuditLog[]
}

interface DatabaseHealth {
  status: {
    configured: boolean
    enabled: boolean
    last_synced_at?: string | null
    last_error?: string | null
    missing_tables?: string[]
    target?: {
      host?: string | null
      database?: string | null
      user?: string | null
    } | null
  }
  connection: {
    configured: boolean
    enabled: boolean
    ok: boolean
    error?: string
    target?: {
      host?: string | null
      database?: string | null
      user?: string | null
    } | null
    tables?: {
      ok: boolean
      missing_tables: string[]
      checked_tables: string[]
    }
  }
}

const tabs: Array<{ id: TabId; label: string; icon: ComponentType<{ className?: string }> }> = [
  { id: 'overview', label: 'Visão geral', icon: Activity },
  { id: 'organizations', label: 'Organizações', icon: Building2 },
  { id: 'users', label: 'Usuários', icon: Users },
  { id: 'connections', label: 'Conexões', icon: Plug },
  { id: 'billing', label: 'Cobrança', icon: CreditCard },
  { id: 'impersonation', label: 'Impersonação', icon: Eye },
  { id: 'audit', label: 'Auditoria', icon: Shield },
  { id: 'platform', label: 'Plataforma', icon: Settings }
]

const providerLabels: Record<string, string> = {
  wavoip: 'Wavoip',
  elevenlabs: 'ElevenLabs',
  twilio: 'Twilio',
  nvoip: 'Nvoip',
  clicksend: 'ClickSend',
  evolution: 'Evolution',
  smoke: 'Smoke'
}

const providerSetup: Record<
  string,
  {
    config: Array<{ key: string; label: string; placeholder: string }>
    secretLabel: string
    secretKey: string
    helper: string
  }
> = {
  wavoip: {
    config: [
      { key: 'instance_name', label: 'Instância', placeholder: 'Beatriz Ramos' },
      { key: 'host', label: 'Host SIP', placeholder: 'sipv2.wavoip.com' },
      { key: 'transport', label: 'Transporte no painel', placeholder: 'UDP' }
    ],
    secretLabel: 'Token SIP da instância',
    secretKey: 'token',
    helper: 'Use o token da instância Wavoip como credencial SIP quando o painel estiver no modo padrão.'
  },
  elevenlabs: {
    config: [
      { key: 'trunk_host', label: 'Host do trunk', placeholder: 'sipv2.wavoip.com' },
      { key: 'agent_phone_number_id', label: 'Phone number ID', placeholder: 'phnum_...' },
      { key: 'default_agent_id', label: 'Agente padrão', placeholder: 'agent_...' }
    ],
    secretLabel: 'ElevenLabs API key',
    secretKey: 'api_key',
    helper: 'A chave fica salva no backend; a UI nunca recebe o valor de volta.'
  },
  twilio: {
    config: [
      { key: 'account_sid', label: 'Account SID', placeholder: 'AC...' },
      { key: 'from_number', label: 'Número de origem', placeholder: '+15551234567' }
    ],
    secretLabel: 'Auth Token',
    secretKey: 'auth_token',
    helper: 'Use o Auth Token real do Console Twilio. Nao cole Account SID neste campo.'
  },
  nvoip: {
    config: [{ key: 'from_number', label: 'Número de origem', placeholder: '+5511999999999' }],
    secretLabel: 'Token/API key',
    secretKey: 'api_key',
    helper: 'Preparado para voz e SMS por organização.'
  },
  clicksend: {
    config: [
      { key: 'username', label: 'Usuário', placeholder: 'usuario@clicksend' },
      { key: 'from_number', label: 'Sender', placeholder: 'AICall' }
    ],
    secretLabel: 'API key',
    secretKey: 'api_key',
    helper: 'Usado para SMS, email e campanhas de voz.'
  },
  evolution: {
    config: [
      { key: 'base_url', label: 'URL da API', placeholder: 'https://evolution.example.com' },
      { key: 'instance_name', label: 'Instância', placeholder: 'minha-instancia' }
    ],
    secretLabel: 'API key',
    secretKey: 'api_key',
    helper: 'Usado para WhatsApp e eventos de mensagem.'
  },
  smoke: {
    config: [{ key: 'endpoint', label: 'Endpoint', placeholder: 'smoke.local' }],
    secretLabel: 'Token de teste',
    secretKey: 'token',
    helper: 'Conexão interna para smoke tests.'
  }
}

function formatDate(value?: string | null) {
  if (!value) return 'Sem registro'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value))
}

function formatRelative(value?: string | null): string {
  if (!value) return 'Sem registro'
  const diff = Date.now() - new Date(value).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'agora mesmo'
  if (mins < 60) return `há ${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `há ${hours}h`
  const days = Math.floor(hours / 24)
  return `há ${days}d`
}

function shortUuid(id: string): string {
  return id.length > 8 ? id.slice(0, 8) + '…' : id
}

function buildProviderDrafts(
  previous: Record<string, ProviderDraft>,
  connectionList: ProviderConnection[]
) {
  return connectionList.reduce<Record<string, ProviderDraft>>((drafts, connection) => {
    const setup = providerSetup[connection.provider]
    const config = (setup?.config || []).reduce<Record<string, string>>((acc, field) => {
      acc[field.key] = String(connection.config?.[field.key] ?? previous[connection.id]?.config?.[field.key] ?? '')
      return acc
    }, {})

    drafts[connection.id] = {
      config,
      secret: previous[connection.id]?.secret || '',
      live: previous[connection.id]?.live || false
    }
    return drafts
  }, {})
}

function compactConfig(config: Record<string, string>) {
  return Object.entries(config).reduce<Record<string, string>>((acc, [key, value]) => {
    const trimmed = value.trim()
    if (trimmed) acc[key] = trimmed
    return acc
  }, {})
}

function checkClassName(status: ProviderCheck['status']) {
  if (status === 'pass') return 'border-success-200 bg-success-50 text-success-900'
  if (status === 'fail') return 'border-danger-200 bg-danger-50 text-danger-900'
  if (status === 'warn') return 'border-warning-200 bg-warning-50 text-warning-900'
  return 'border-slate-200 bg-slate-50 text-slate-600'
}

function getApiErrorMessage(error: unknown, fallback: string) {
  if (typeof error !== 'object' || error === null || !('response' in error)) return fallback
  const response = (error as { response?: { data?: { error?: unknown; message?: unknown } } }).response
  const message = response?.data?.error || response?.data?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

function formatDatabaseMessage(message?: string | null) {
  if (!message) return ''
  if (message.includes('SUPABASE_DB_URL')) {
    return 'Conexão do banco operacional ausente. Configure a URL do banco no backend.'
  }
  if (message.includes('SUPABASE_MIRROR_ENABLED') || message.includes('SAAS_STORAGE')) {
    return 'Sincronização com o banco operacional precisa estar ativa no backend.'
  }
  return message
}

function auditActionBadgeClass(action: string): string {
  if (action.startsWith('billing.')) return 'badge-warning'
  if (action.startsWith('admin.')) return 'badge-danger'
  if (action.startsWith('call.')) return 'badge-info'
  return 'badge'
}

function roleBadgeClass(role: string): string {
  const r = role.toLowerCase()
  if (r === 'owner') return 'badge-danger'
  if (r === 'admin') return 'badge-warning'
  if (r === 'operator') return 'badge-info'
  return 'badge'
}

function StatusBadge({ value }: { value: string }) {
  const safe = value || 'unknown'
  const normalized = safe.toLowerCase()
  const className =
    normalized.includes('ready') ||
    normalized.includes('active') ||
    normalized.includes('healthy') ||
    normalized.includes('trial') ||
    normalized.includes('online') ||
    normalized.includes('enabled')
      ? 'badge-success'
      : normalized.includes('planned') ||
          normalized.includes('invited') ||
          normalized.includes('pending') ||
          normalized.includes('dry')
        ? 'badge-warning'
        : 'badge-danger'

  return <span className={className}>{safe}</span>
}

function MetricCard({
  icon: Icon,
  iconColor,
  label,
  value,
  detail
}: {
  icon: ComponentType<{ className?: string }>
  iconColor?: string
  label: string
  value: string | number
  detail: string
}) {
  return (
    <div className="metric-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="mt-1 text-2xl font-bold text-slate-950 tabular-nums">{value}</p>
          <p className="mt-1 text-xs font-medium text-slate-500 truncate">{detail}</p>
        </div>
        <div className={`metric-icon shrink-0 ${iconColor ?? 'bg-primary-50 text-primary-600'}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}

function EmptyState({
  icon: Icon,
  title,
  description
}: {
  icon: ComponentType<{ className?: string }>
  title: string
  description: string
}) {
  return (
    <div className="empty-state">
      <Icon className="mx-auto h-8 w-8 text-slate-300" />
      <p className="mt-3 text-sm font-semibold text-slate-700">{title}</p>
      <p className="mt-1 text-xs text-slate-500">{description}</p>
    </div>
  )
}

function WavoipTokenCard({ token }: { token: string }) {
  const [copied, setCopied] = useState(false)
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&margin=12&data=${encodeURIComponent(token)}`

  async function copyToken() {
    try {
      await navigator.clipboard.writeText(token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toastError(error, 'Não foi possível copiar.')
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
      <div className="flex flex-col items-center gap-3">
        <img
          src={qrUrl}
          alt="QR Code do token Wavoip"
          className="h-48 w-48 rounded-lg border border-slate-200 bg-white"
        />
        <div className="min-w-0 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Token do dispositivo</p>
          <p className="mt-1 font-mono text-sm text-slate-800 break-all">{token}</p>
        </div>
      </div>
      <button type="button" className="btn-secondary w-full" onClick={copyToken}>
        {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? 'Copiado!' : 'Copiar Token'}
      </button>
    </div>
  )
}

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab') as TabId | null
  const [activeTab, setActiveTab] = useState<TabId>(tabFromUrl || 'overview')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [summary, setSummary] = useState<AdminSummary | null>(null)
  const [databaseHealth, setDatabaseHealth] = useState<DatabaseHealth | null>(null)
  const [roles, setRoles] = useState<RoleDefinition[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [connections, setConnections] = useState<ProviderConnection[]>([])
  const [providerDrafts, setProviderDrafts] = useState<Record<string, ProviderDraft>>({})
  const [providerTests, setProviderTests] = useState<Record<string, ProviderTestResult>>({})
  const [plans, setPlans] = useState<Plan[]>([])
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [_usage, setUsage] = useState<UsageSummary | null>(null)
  const [impersonations, setImpersonations] = useState<ImpersonationSession[]>([])
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', billing_email: '' })
  const [impersonationTarget, setImpersonationTarget] = useState('')
  const [impersonationReason, setImpersonationReason] = useState('')
  const [showNewOrgForm, setShowNewOrgForm] = useState(false)

  // Invitation state
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' })
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteSaving, setInviteSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null)
  const [banConfirm, setBanConfirm] = useState<AdminUser | null>(null)

  // Platform config state
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null)
  const [platformConfig, setPlatformConfig] = useState<Record<string, string>>({})
  const [platformDraft, setPlatformDraft] = useState<Record<string, string>>({})
  const [platformSaving, setPlatformSaving] = useState(false)

  // Admin billing state
  const [billingSubs, setBillingSubs] = useState<AdminBillingSubscription[]>([])
  const [selectedBillingOrgId, setSelectedBillingOrgId] = useState('')
  const [billingOrgDetail, setBillingOrgDetail] = useState<AdminBillingOrgDetail | null>(null)
  const [billingLoading, setBillingLoading] = useState(false)
  const [extendDays, setExtendDays] = useState(30)
  const [changePlanCode, setChangePlanCode] = useState('')
  const [activateNote, setActivateNote] = useState('')
  const [deactivateNote, setDeactivateNote] = useState('')
  const [limitOverrides, setLimitOverrides] = useState<Record<string, number>>({})
  const [showLimitsForm, setShowLimitsForm] = useState(false)
  const [billingFilterStatus, setBillingFilterStatus] = useState('')

  const activeImpersonations = useMemo(
    () => impersonations.filter((session) => session.status === 'active'),
    [impersonations]
  )

  async function refresh() {
    setLoading(true)
    try {
      const [
        summaryResult,
        databaseResult,
        rolesResult,
        organizationsResult,
        usersResult,
        connectionsResult,
        plansResult,
        subscriptionResult,
        usageResult,
        impersonationsResult,
        auditResult,
        meResult,
        platformConfigResult
      ] = await Promise.allSettled([
        apiService.getAdminSummary(),
        apiService.getAdminDatabase(),
        apiService.getAdminRoles(),
        apiService.getAdminOrganizations(),
        apiService.getAdminUsers(),
        apiService.getProviderConnections(),
        apiService.getBillingPlans(),
        apiService.getBillingSubscription(),
        apiService.getBillingUsage(),
        apiService.getImpersonations(),
        apiService.getAuditLogs(),
        apiService.getMe(),
        apiService.getPlatformConfig()
      ])

      if (summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value.data.data as AdminSummary)
      } else {
        console.error('Falha ao carregar resumo admin:', summaryResult.reason)
      }

      if (databaseResult.status === 'fulfilled') {
        setDatabaseHealth(databaseResult.value.data.data as DatabaseHealth)
      } else {
        console.error('Falha ao carregar saúde do banco:', databaseResult.reason)
      }

      if (rolesResult.status === 'fulfilled') {
        setRoles(rolesResult.value.data.data as RoleDefinition[])
      } else {
        console.error('Falha ao carregar papéis:', rolesResult.reason)
      }

      if (organizationsResult.status === 'fulfilled') {
        setOrganizations(organizationsResult.value.data.data as Organization[])
      } else {
        console.error('Falha ao carregar organizações:', organizationsResult.reason)
      }

      if (usersResult.status === 'fulfilled') {
        const raw: any[] = usersResult.value.data.data
        setUsers((raw || []).map((u) => ({
          id: u.user_id || u.id,
          name: u.user_profiles?.full_name || u.name || '',
          email: u.user_profiles?.email || u.email || '',
          status: u.status || 'active',
          mfa_enabled: u.user_profiles?.mfa_enabled || false,
          last_login_at: u.user_profiles?.last_login_at || u.last_login_at || null,
          membership: {
            id: u.id,
            organization_id: u.organization_id,
            user_id: u.user_id,
            role: u.role,
            status: u.status || 'active'
          }
        })))
      } else {
        console.error('Falha ao carregar usuários:', usersResult.reason)
      }

      if (connectionsResult.status === 'fulfilled') {
        const raw = (connectionsResult.value.data.data || []) as any[]
        const connectionData = raw.map((c) => ({
          ...c,
          capabilities: c.capabilities || [],
          health: c.health || 'unknown',
          secret_status: c.secret_status || 'unknown',
          last_checked_at: c.last_checked_at || c.last_health_check_at || null
        })) as ProviderConnection[]
        setConnections(connectionData)
        setProviderDrafts((current) => buildProviderDrafts(current, connectionData))
      } else {
        console.error('Falha ao carregar conexões:', connectionsResult.reason)
      }

      if (plansResult.status === 'fulfilled') {
        setPlans(plansResult.value.data.data as Plan[])
      } else {
        console.error('Falha ao carregar planos:', plansResult.reason)
      }

      if (subscriptionResult.status === 'fulfilled') {
        setSubscription(subscriptionResult.value.data.data as Subscription)
      } else {
        console.error('Falha ao carregar assinatura:', subscriptionResult.reason)
      }

      if (usageResult.status === 'fulfilled') {
        setUsage(usageResult.value.data.data as UsageSummary)
      } else {
        console.error('Falha ao carregar uso:', usageResult.reason)
      }

      if (impersonationsResult.status === 'fulfilled') {
        setImpersonations(impersonationsResult.value.data.data as ImpersonationSession[])
      } else {
        console.error('Falha ao carregar impersonações:', impersonationsResult.reason)
      }

      if (auditResult.status === 'fulfilled') {
        setAuditLogs(auditResult.value.data.data as AuditLog[])
      } else {
        console.error('Falha ao carregar auditoria:', auditResult.reason)
      }

      if (meResult.status === 'fulfilled') {
        setCurrentUser(meResult.value.data?.data?.user || null)
      } else {
        console.error('Falha ao carregar usuário:', meResult.reason)
      }

      if (platformConfigResult.status === 'fulfilled') {
        const raw = platformConfigResult.value.data?.data
        const map: Record<string, string> = {}
        if (Array.isArray(raw)) {
          raw.forEach((item: { key?: string; value?: string }) => {
            if (item.key) map[item.key] = item.value || ''
          })
        } else if (raw && typeof raw === 'object') {
          Object.entries(raw).forEach(([k, v]) => {
            map[k] = String(v ?? '')
          })
        }
        setPlatformConfig(map)
        setPlatformDraft({ ...map })
      } else {
        console.error('Falha ao carregar config da plataforma:', platformConfigResult.reason)
      }
    } catch (error) {
      toastError(error, 'Não foi possível carregar o administrativo.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    loadInvitations()
  }, [])

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl])

  async function runAction(action: () => Promise<unknown>, successMessage: string) {
    setSaving(true)
    try {
      await action()
      toast.success(successMessage)
      await refresh()
    } catch (error) {
      console.error('Falha administrativa:', error)
      toast.error(getApiErrorMessage(error, 'Ação administrativa falhou. Veja o console e auditoria.'))
    } finally {
      setSaving(false)
    }
  }

  async function loadInvitations() {
    try {
      const res = await apiService.getAdminInvitations()
      setInvitations(res.data?.data || [])
    } catch (error) {
      console.error('Falha ao carregar convites:', error)
    }
  }

  async function handleCreateInvite() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(inviteForm.email)) {
      toast.error('Email inválido')
      return
    }
    setInviteSaving(true)
    try {
      const res = await apiService.createAdminInvitation(inviteForm)
      const inviteUrl = res.data?.data?.invite_url
      if (inviteUrl) {
        toast.success('Convite criado! Copie o link abaixo.', { duration: 8000 })
        try { await navigator.clipboard.writeText(inviteUrl) } catch { /* ignore */ }
      } else {
        toast.success('Convite enviado!')
      }
      setInviteForm({ email: '', role: 'member' })
      setShowInviteModal(false)
      await loadInvitations()
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Falha ao criar convite.'))
    } finally {
      setInviteSaving(false)
    }
  }

  async function handleResendInvite(id: string) {
    await runAction(() => apiService.resendAdminInvitation(id), 'Convite reenviado.')
  }

  async function handleRevokeInvite(id: string) {
    await runAction(() => apiService.revokeAdminInvitation(id), 'Convite revogado.')
  }

  async function handleDeleteUser(user: AdminUser) {
    setSaving(true)
    try {
      await apiService.deleteAdminUser(user.id)
      toast.success('Usuário removido da organização.')
      setDeleteConfirm(null)
      await refresh()
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Falha ao remover usuário.'))
    } finally {
      setSaving(false)
    }
  }

  async function handleBanUser(user: AdminUser) {
    setSaving(true)
    try {
      await apiService.banAdminUser(user.id)
      toast.success('Usuário banido da organização.')
      setBanConfirm(null)
      await refresh()
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Falha ao banir usuário.'))
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateOrganization() {
    await runAction(async () => {
      await apiService.createAdminOrganization(newOrg)
      setNewOrg({ name: '', slug: '', billing_email: '' })
      setShowNewOrgForm(false)
    }, 'Organização criada.')
  }

  async function handleStartImpersonation() {
    await runAction(async () => {
      await apiService.startImpersonation({
        target_user_id: impersonationTarget,
        reason: impersonationReason,
        duration_minutes: 30
      })
      setImpersonationReason('')
    }, 'Impersonação iniciada com auditoria.')
  }

  function updateProviderDraft(connectionId: string, field: string, value: string) {
    setProviderDrafts((current) => ({
      ...current,
      [connectionId]: {
        ...(current[connectionId] || { config: {}, secret: '', live: false }),
        config: {
          ...(current[connectionId]?.config || {}),
          [field]: value
        }
      }
    }))
  }

  function updateProviderSecret(connectionId: string, value: string) {
    setProviderDrafts((current) => ({
      ...current,
      [connectionId]: {
        ...(current[connectionId] || { config: {}, secret: '', live: false }),
        secret: value
      }
    }))
  }

  function toggleProviderLive(connectionId: string) {
    setProviderDrafts((current) => ({
      ...current,
      [connectionId]: {
        ...(current[connectionId] || { config: {}, secret: '', live: false }),
        live: !current[connectionId]?.live
      }
    }))
  }

  async function handleTestProviderConnection(connection: ProviderConnection) {
    const draft = providerDrafts[connection.id] || { config: {}, secret: '', live: false }
    const setup = providerSetup[connection.provider]
    const secrets = draft.secret.trim() && setup ? { [setup.secretKey]: draft.secret.trim() } : {}

    setSaving(true)
    try {
      const response = await apiService.testProviderConnection(connection.id, {
        config: compactConfig(draft.config),
        secrets,
        live: draft.live
      })
      const result = response.data.data as ProviderTestResult
      setProviderTests((current) => ({ ...current, [connection.id]: result }))
      if (result.ok) {
        toast.success('Conexão validada.')
        try {
          const connectionsResponse = await apiService.getProviderConnections()
          const connectionData = connectionsResponse.data.data as ProviderConnection[]
          setConnections(connectionData)
          setProviderDrafts((current) => buildProviderDrafts(current, connectionData))
        } catch (refreshError) {
          console.error('Falha ao recarregar conexões após teste:', refreshError)
        }
      } else {
        toast.error('Conexão ainda precisa de ajuste.')
      }
    } catch (error) {
      console.error('Falha ao testar conexão:', error)
      toast.error(getApiErrorMessage(error, 'Ação administrativa falhou. Veja o console e auditoria.'))
    } finally {
      setSaving(false)
    }
  }

  // Admin billing handlers
  async function loadBillingSubs() {
    setBillingLoading(true)
    try {
      const params = billingFilterStatus ? { status: billingFilterStatus } : undefined
      const res = await apiService.getAdminBillingSubscriptions(params)
      const subs = (res.data?.data || []) as AdminBillingSubscription[]
      setBillingSubs(subs)
    } catch (error) {
      toastError(error, 'Erro ao carregar assinaturas')
    } finally {
      setBillingLoading(false)
    }
  }

  async function loadBillingOrgDetail(orgId: string) {
    if (!orgId) {
      setBillingOrgDetail(null)
      return
    }
    setBillingLoading(true)
    try {
      const res = await apiService.getAdminBillingOrgDetail(orgId)
      setBillingOrgDetail(res.data?.data as AdminBillingOrgDetail)
    } catch (error) {
      toastError(error, 'Erro ao carregar detalhes da organização')
    } finally {
      setBillingLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'billing') {
      loadBillingSubs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useEffect(() => {
    if (selectedBillingOrgId) {
      loadBillingOrgDetail(selectedBillingOrgId)
    }
  }, [selectedBillingOrgId])

  async function handleActivateSubscription(orgId: string) {
    await runAction(async () => {
      await apiService.activateAdminBillingSubscription(orgId, { note: activateNote || undefined })
      setActivateNote('')
      await loadBillingOrgDetail(orgId)
      await loadBillingSubs()
    }, 'Assinatura ativada manualmente.')
  }

  async function handleDeactivateSubscription(orgId: string) {
    await runAction(async () => {
      await apiService.deactivateAdminBillingSubscription(orgId, { note: deactivateNote || undefined })
      setDeactivateNote('')
      await loadBillingOrgDetail(orgId)
      await loadBillingSubs()
    }, 'Assinatura desativada.')
  }

  async function handleChangePlan(orgId: string) {
    if (!changePlanCode) {
      toast.error('Selecione um plano')
      return
    }
    await runAction(async () => {
      await apiService.changeAdminBillingPlan(orgId, { plan_code: changePlanCode, note: `Troca manual para ${changePlanCode}` })
      setChangePlanCode('')
      await loadBillingOrgDetail(orgId)
      await loadBillingSubs()
    }, 'Plano alterado.')
  }

  async function handleExtendPeriod(orgId: string) {
    await runAction(async () => {
      await apiService.extendAdminBillingPeriod(orgId, { days: extendDays })
      await loadBillingOrgDetail(orgId)
      await loadBillingSubs()
    }, `Período estendido em ${extendDays} dias.`)
  }

  async function handleOverrideLimits(orgId: string) {
    await runAction(async () => {
      await apiService.updateAdminBillingLimits(orgId, { limits: limitOverrides })
      await loadBillingOrgDetail(orgId)
      await loadBillingSubs()
      setShowLimitsForm(false)
      setLimitOverrides({})
    }, 'Limites sobrescritos com sucesso.')
  }

  async function handleSavePlatformConfig() {
    setPlatformSaving(true)
    try {
      const fields = [
        { key: 'elevenlabs_api_key', value: platformDraft.elevenlabs_api_key || '' },
        { key: 'elevenlabs_base_url', value: platformDraft.elevenlabs_base_url || '' },
        { key: 'wavoip_device_token', value: platformDraft.wavoip_device_token || '' },
        { key: 'wavoip_sip_host', value: platformDraft.wavoip_sip_host || '' },
        { key: 'wavoip_phone_number', value: platformDraft.wavoip_phone_number || '' },
        { key: 'evolution_url', value: platformDraft.evolution_url || '' },
        { key: 'evolution_api_key', value: platformDraft.evolution_api_key || '' },
        { key: 'abacatepay_api_key', value: platformDraft.abacatepay_api_key || '' },
        { key: 'abacatepay_base_url', value: platformDraft.abacatepay_base_url || '' },
        { key: 'abacatepay_webhook_secret', value: platformDraft.abacatepay_webhook_secret || '' },
        { key: 'abacatepay_return_url', value: platformDraft.abacatepay_return_url || '' }
      ]
      await Promise.all(fields.map((f) => apiService.updatePlatformConfig(f)))
      toast.success('Configurações salvas.')
      const res = await apiService.getPlatformConfig()
      const raw = res.data?.data
      const map: Record<string, string> = {}
      if (Array.isArray(raw)) {
        raw.forEach((item: { key?: string; value?: string }) => {
          if (item.key) map[item.key] = item.value || ''
        })
      } else if (raw && typeof raw === 'object') {
        Object.entries(raw).forEach(([k, v]) => {
          map[k] = String(v ?? '')
        })
      }
      setPlatformConfig(map)
      setPlatformDraft({ ...map })
    } catch (error) {
      console.error('Falha ao salvar config da plataforma:', error)
      toast.error(getApiErrorMessage(error, 'Falha ao salvar configurações.'))
    } finally {
      setPlatformSaving(false)
    }
  }

  if (loading && !summary) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando administrativo SaaS...
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin</h1>
          <p className="page-description">
            Controle multiusuário, impersonação auditada, provedores de voz/SMS/IA e cobrança.
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={loading} className="btn-secondary">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Active impersonation banner */}
      {activeImpersonations.length > 0 && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-900">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 font-semibold">
              <Eye className="h-4 w-4" />
              {activeImpersonations.length} sessão de impersonação ativa
            </div>
            <span className="text-xs font-medium">
              Ações sensíveis ficam bloqueadas e tudo é registrado na auditoria.
            </span>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex overflow-x-auto border-b border-slate-200 mb-6 scrollbar-thin">
        {tabs
          .filter((tab) => {
            if (tab.id === 'platform' || tab.id === 'organizations' || tab.id === 'billing' || tab.id === 'impersonation') {
              return currentUser?.is_platform_admin === true
            }
            return true
          })
          .map((tab) => {
            const Icon = tab.icon
            return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id)
                    setSearchParams(tab.id === 'overview' ? {} : { tab: tab.id })
                  }}
                  className={[
                  'inline-flex shrink-0 items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition cursor-pointer whitespace-nowrap',
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                ].join(' ')}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
      </div>

      {/* ── Overview tab ── */}
      {activeTab === 'overview' && summary && (
        <div className="space-y-6">
          {/* Top metrics grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
              icon={Building2}
              iconColor="bg-primary-50 text-primary-600"
              label="Organização"
              value={summary.organization?.name || 'Sem conta'}
              detail={summary.organization?.status || 'sem status'}
            />
            <MetricCard
              icon={Users}
              iconColor="bg-success-50 text-success-600"
              label="Usuários"
              value={summary.users.total}
              detail={`${summary.users.active} ativos · ${summary.users.invited} convidados`}
            />
            <MetricCard
              icon={Plug}
              iconColor="bg-info-50 text-info-600"
              label="Conexões"
              value={`${summary.providers.ready}/${summary.providers.total}`}
              detail={`${summary.providers.planned} planejadas`}
            />
            <MetricCard
              icon={Shield}
              iconColor="bg-warning-50 text-warning-600"
              label="Auditoria"
              value={summary.security.audit_events}
              detail={`${summary.security.active_impersonations} impersonações ativas`}
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            {/* Operational health */}
            <section className="surface">
              <div className="panel-header">
                <div>
                  <h2 className="panel-title">Saúde operacional</h2>
                  <p className="panel-subtitle">Provedores principais e próximos canais de expansão.</p>
                </div>
              </div>
              {connections.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon={Plug}
                    title="Nenhuma conexão cadastrada"
                    description="Vá até a aba Conexões para configurar provedores de voz, SMS e IA."
                  />
                </div>
              ) : (
                <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
                  {connections.map((connection) => (
                    <div key={connection.id} className="rounded-lg border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-950 truncate">
                            {providerLabels[connection.provider] || connection.name}
                          </h3>
                          <p className="mt-1 text-xs text-slate-500 truncate">{connection.capabilities.join(', ')}</p>
                        </div>
                        {connection.health === 'healthy' ? (
                          <CheckCircle2 className="h-5 w-5 shrink-0 text-success-600" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 shrink-0 text-warning-600" />
                        )}
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <StatusBadge value={connection.status} />
                        <StatusBadge value={connection.health} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Plan and usage */}
            <section className="surface">
              <div className="panel-header">
                <div>
                  <h2 className="panel-title">Plano e uso</h2>
                  <p className="panel-subtitle">Base para cobrança fixa e excedentes.</p>
                </div>
              </div>
              <div className="space-y-4 p-4">
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Plano atual</p>
                      <p className="text-xl font-bold text-slate-950">{summary.billing.plan?.name || 'Sem plano'}</p>
                    </div>
                    <StatusBadge value={subscription?.status || 'sem assinatura'} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Chamadas</p>
                    <p className="mt-1 text-xl font-bold text-slate-950 tabular-nums">{summary.billing.usage.voice_call || 0}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Minutos</p>
                    <p className="mt-1 text-xl font-bold text-slate-950 tabular-nums">{summary.billing.usage.voice_minute || 0}</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Platform health */}
          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Saúde da plataforma</h2>
                <p className="panel-subtitle">
                  Estado do armazenamento operacional, sincronização de dados e tabelas esperadas para o SaaS.
                </p>
              </div>
              <div className="metric-icon bg-slate-100 text-slate-700">
                <Database className="h-5 w-5" />
              </div>
            </div>
            <div className="grid gap-3 p-4 md:grid-cols-3">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Banco operacional</p>
                <div className="mt-2">
                  <StatusBadge value={databaseHealth?.connection.ok ? 'online' : 'pendente'} />
                </div>
                <p className="mt-3 text-sm text-slate-600 truncate">
                  {databaseHealth?.connection.target?.host || 'Configure a conexão de banco no backend.'}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sincronização</p>
                <div className="mt-2">
                  <StatusBadge value={databaseHealth?.status.enabled ? 'enabled' : 'disabled'} />
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  Última sync: {formatDate(databaseHealth?.status.last_synced_at)}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Schema</p>
                <p className="mt-2 text-2xl font-bold text-slate-950 tabular-nums">
                  {databaseHealth?.connection.tables?.missing_tables?.length ?? databaseHealth?.status.missing_tables?.length ?? 0}
                </p>
                <p className="mt-1 text-sm text-slate-600">tabelas pendentes</p>
              </div>
            </div>
            {databaseHealth?.connection.error && (
              <div className="mx-4 mb-4 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-900">
                {formatDatabaseMessage(databaseHealth.connection.error)}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Organizations tab ── */}
      {activeTab === 'organizations' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div />
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowNewOrgForm((v) => !v)}
            >
              <Building2 className="h-4 w-4" />
              Nova Organização
            </button>
          </div>

          {showNewOrgForm && (
            <section className="surface">
              <div className="panel-header">
                <div>
                  <h2 className="panel-title">Nova organização</h2>
                  <p className="panel-subtitle">Cria um tenant multi-org no SaaS.</p>
                </div>
              </div>
              <div className="space-y-3 p-4">
                <input
                  className="input"
                  placeholder="Nome da organização"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="slug (ex: acme-corp)"
                  value={newOrg.slug}
                  onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="billing@empresa.com"
                  value={newOrg.billing_email}
                  onChange={(e) => setNewOrg({ ...newOrg, billing_email: e.target.value })}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={saving || !newOrg.name}
                    onClick={handleCreateOrganization}
                  >
                    <Building2 className="h-4 w-4" />
                    Criar organização
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setShowNewOrgForm(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </section>
          )}

          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Organizações</h2>
                <p className="panel-subtitle">Tenants cadastrados no SaaS.</p>
              </div>
            </div>
            {organizations.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={Building2}
                  title="Nenhuma organização cadastrada"
                  description="Crie a primeira organização usando o botão acima."
                />
              </div>
            ) : (
              <div className="table-shell m-4">
                <table className="w-full text-left text-sm">
                  <thead className="table-head">
                    <tr>
                      <th className="px-4 py-3">Nome</th>
                      <th className="px-4 py-3">Slug</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">E-mail de cobrança</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {organizations.map((org) => (
                      <tr key={org.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-950 truncate max-w-[180px]">{org.name}</p>
                          <p
                            className="text-xs text-slate-400 font-mono truncate max-w-[180px]"
                            title={org.id}
                          >
                            {shortUuid(org.id)}
                          </p>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-slate-600 truncate max-w-[120px]">{org.slug}</td>
                        <td className="px-4 py-3">
                          <StatusBadge value={org.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 truncate max-w-[200px]">
                          {org.billing_email || '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            aria-label={`Excluir organização ${org.name}`}
                            className="btn-icon text-danger-600 hover:text-danger-800 hover:bg-danger-50"
                            onClick={() => {
                              if (window.confirm(`Excluir organização "${org.name}"?`)) {
                                runAction(
                                  () => apiService.deleteAdminOrganization(org.id),
                                  'Organização excluída.'
                                )
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Users tab ── */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div />
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowInviteModal(true)}
            >
              <UserPlus className="h-4 w-4" />
              Convidar
            </button>
          </div>

          {/* Invite modal */}
          {showInviteModal && (
            <section className="surface relative">
              <div className="panel-header">
                <div>
                  <h2 className="panel-title">Convidar usuário</h2>
                  <p className="panel-subtitle">O convite expira em 7 dias.</p>
                </div>
              </div>
              <div className="space-y-3 p-4">
                <input
                  className="input"
                  type="email"
                  placeholder="E-mail do convidado"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                />
                <select
                  className="input"
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={inviteSaving || !inviteForm.email}
                    onClick={handleCreateInvite}
                  >
                    {inviteSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    Enviar convite
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setShowInviteModal(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Members */}
          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Membros</h2>
                <p className="panel-subtitle">Usuários ativos na organização.</p>
              </div>
            </div>
            {users.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={Users}
                  title="Nenhum membro"
                  description="Convide usuários usando o botão acima."
                />
              </div>
            ) : (
              <div className="table-shell m-4 overflow-x-auto">
                <table className="min-w-[880px] w-full text-left text-sm">
                  <thead className="table-head">
                    <tr>
                      <th className="px-4 py-3">Usuário</th>
                      <th className="px-4 py-3">E-mail</th>
                      <th className="px-4 py-3">Papel</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Último acesso</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-950 truncate max-w-[140px]">{user.name}</p>
                          <p className="text-xs text-slate-400 font-mono" title={user.id}>
                            {shortUuid(user.id)}
                          </p>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-slate-600 truncate max-w-[200px]">
                          {user.email}
                        </td>
                        <td className="px-4 py-3">
                          <span className={roleBadgeClass(user.membership?.role || 'member')}>
                            {user.membership?.role || 'member'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge value={user.status} />
                        </td>
                        <td className="px-4 py-3 text-slate-600 tabular-nums">{formatDate(user.last_login_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              aria-label={`Impersonar ${user.name}`}
                              className="btn-icon"
                              onClick={() => {
                                setImpersonationTarget(user.id)
                                setActiveTab('impersonation')
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Banir ${user.name}`}
                              className="btn-icon text-warning-600 hover:bg-warning-50"
                              onClick={() => setBanConfirm(user)}
                            >
                              <StopCircle className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Remover ${user.name}`}
                              className="btn-icon text-danger-600 hover:bg-danger-50"
                              onClick={() => setDeleteConfirm(user)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Pending invitations */}
          {invitations.length > 0 && (
            <section className="surface">
              <div className="panel-header">
                <div>
                  <h2 className="panel-title">Convites pendentes</h2>
                  <p className="panel-subtitle">Aguardando aceitação.</p>
                </div>
              </div>
              <div className="table-shell m-4 overflow-x-auto">
                <table className="min-w-[700px] w-full text-left text-sm">
                  <thead className="table-head">
                    <tr>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Papel</th>
                      <th className="px-4 py-3">Expira</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {invitations
                      .filter((inv) => inv.status === 'pending')
                      .map((inv) => (
                        <tr key={inv.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 font-mono text-sm text-slate-600">
                            {inv.email}
                          </td>
                          <td className="px-4 py-3">
                            <span className={roleBadgeClass(inv.role)}>{inv.role}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-600 tabular-nums">
                            {formatDate(inv.expires_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                className="btn-ghost text-xs"
                                onClick={() => handleResendInvite(inv.id)}
                                disabled={saving}
                              >
                                Reenviar
                              </button>
                              <button
                                type="button"
                                className="btn-ghost text-xs text-danger-600"
                                onClick={() => handleRevokeInvite(inv.id)}
                                disabled={saving}
                              >
                                Revogar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Delete confirmation modal */}
          {deleteConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="mx-4 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg">
                <h3 className="text-lg font-bold text-slate-900">Remover usuário</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Deseja remover <strong>{deleteConfirm.name}</strong> ({deleteConfirm.email}) da organização?
                  Isso desativa o acesso imediatamente.
                </p>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setDeleteConfirm(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    disabled={saving}
                    onClick={() => handleDeleteUser(deleteConfirm)}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Remover
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Ban confirmation modal */}
          {banConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="mx-4 w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg">
                <h3 className="text-lg font-bold text-slate-900">Banir usuário</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Deseja banir <strong>{banConfirm.name}</strong> ({banConfirm.email}) da organização?
                  O acesso será desativado imediatamente.
                </p>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setBanConfirm(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    disabled={saving}
                    onClick={() => handleBanUser(banConfirm)}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <StopCircle className="h-4 w-4" />}
                    Banir
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Connections tab ── */}
      {activeTab === 'connections' && (
        <section className="surface">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Conexões de voz, SMS e IA</h2>
              <p className="panel-subtitle">
                Cada organização cadastra suas chaves aqui. Os segredos são enviados ao backend e não voltam para a tela.
              </p>
            </div>
          </div>
          {connections.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={Plug}
                title="Nenhuma conexão disponível"
                description="Nenhum provedor foi cadastrado para essa organização."
              />
            </div>
          ) : (
            <div className="grid gap-4 p-4 xl:grid-cols-2">
              {connections.map((connection) => {
                const setup = providerSetup[connection.provider] || providerSetup.smoke
                const draft = providerDrafts[connection.id] || { config: {}, secret: '', live: false }
                const testResult = providerTests[connection.id]
                const isHealthy = connection.health === 'healthy'

                return (
                  <div key={connection.id} className="card">
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Status dot */}
                        <span
                          className={[
                            'h-2.5 w-2.5 rounded-full shrink-0',
                            isHealthy
                              ? 'bg-success-500'
                              : connection.status === 'planned'
                                ? 'bg-warning-400 animate-pulse'
                                : 'bg-danger-500'
                          ].join(' ')}
                        />
                        <div className="min-w-0">
                          <h3 className="text-base font-bold text-slate-950 truncate">
                            {providerLabels[connection.provider] || connection.name}
                          </h3>
                          <p className="text-xs text-slate-500 truncate">{connection.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge value={connection.status} />
                      </div>
                    </div>

                    {/* Capabilities */}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {connection.capabilities.map((cap) => (
                        <span key={cap} className="badge-info">{cap}</span>
                      ))}
                    </div>

                    {/* Meta row */}
                    <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <dt className="text-slate-500">Saúde</dt>
                        <dd className="font-semibold text-slate-900 mt-0.5">
                          {isHealthy
                            ? <Wifi className="inline h-3.5 w-3.5 text-success-600" />
                            : <WifiOff className="inline h-3.5 w-3.5 text-danger-500" />}
                          {' '}{connection.health}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Segredos</dt>
                        <dd className="font-semibold text-slate-900 mt-0.5">{connection.secret_status}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Último check</dt>
                        <dd className="font-semibold text-slate-900 mt-0.5 tabular-nums">{formatDate(connection.last_checked_at)}</dd>
                      </div>
                    </dl>

                    {/* Config fields */}
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {setup.config.map((field) => (
                        <label key={field.key} className="space-y-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{field.label}</span>
                          <input
                            className="input"
                            value={draft.config[field.key] || ''}
                            placeholder={field.placeholder}
                            onChange={(e) => updateProviderDraft(connection.id, field.key, e.target.value)}
                          />
                        </label>
                      ))}
                      <label className="space-y-1 sm:col-span-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{setup.secretLabel}</span>
                        <input
                          className="input"
                          type="password"
                          value={draft.secret}
                          placeholder={
                            connection.secret_status === 'configured'
                              ? 'Já configurado; preencha só para trocar'
                              : 'Cole a chave/token'
                          }
                          onChange={(e) => updateProviderSecret(connection.id, e.target.value)}
                        />
                      </label>
                    </div>

                    {/* Existing config key-values */}
                    {Object.keys(connection.config).length > 0 && (
                      <div className="mt-3 space-y-1">
                        {Object.entries(connection.config).map(([k, v]) => (
                          <div key={k} className="bg-slate-50 font-mono text-xs rounded p-2 flex justify-between gap-2 overflow-hidden">
                            <span className="text-slate-500 shrink-0">{k}</span>
                            <span className="text-slate-800 truncate">{String(v ?? '—')}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="mt-3 text-xs leading-5 text-slate-500">{setup.helper}</p>

                    {/* Actions row */}
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={draft.live}
                          onChange={() => toggleProviderLive(connection.id)}
                        />
                        Teste live quando disponível
                      </label>
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={saving}
                        onClick={() => handleTestProviderConnection(connection)}
                      >
                        <RefreshCw className="h-4 w-4" />
                        Testar Conexão
                      </button>
                    </div>

                    {/* Wavoip token display in connection card */}
                    {connection.provider === 'wavoip' && platformConfig.wavoip_device_token && (
                      <div className="mt-4">
                        <WavoipTokenCard token={platformConfig.wavoip_device_token} />
                      </div>
                    )}

                    {/* Test result */}
                    {testResult && (
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center gap-2">
                          {testResult.ok
                            ? <span className="badge-success">Passou</span>
                            : <span className="badge-danger">Falhou</span>}
                        </div>
                        {testResult.checks.map((item) => (
                          <div
                            key={item.code}
                            className={`rounded-lg border px-3 py-2 text-xs ${checkClassName(item.status)}`}
                          >
                            <span className="font-semibold uppercase">{item.status}</span> · {item.message}
                          </div>
                        ))}
                        {testResult.recommendations.map((rec) => (
                          <p key={rec} className="text-xs leading-5 text-slate-500">{rec}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      {/* ── Billing tab ── */}
      {activeTab === 'billing' && (
        <div className="grid gap-6 xl:grid-cols-[1fr_0.85fr]">
          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Todas as assinaturas</h2>
                <p className="panel-subtitle">Gerencie assinaturas de todas as organizações.</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="input text-sm"
                  value={billingFilterStatus}
                  onChange={(e) => setBillingFilterStatus(e.target.value)}
                >
                  <option value="">Todos os status</option>
                  <option value="active">Ativo</option>
                  <option value="pending_payment">Pagamento pendente</option>
                  <option value="expired">Expirado</option>
                  <option value="canceled">Cancelado</option>
                </select>
                <button type="button" className="btn-secondary" onClick={loadBillingSubs} disabled={billingLoading}>
                  <RefreshCw className={`h-4 w-4 ${billingLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            {billingLoading && billingSubs.length === 0 ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : billingSubs.length === 0 ? (
              <div className="p-4">
                <EmptyState icon={CreditCard} title="Nenhuma assinatura" description="Nenhuma assinatura encontrada com o filtro atual." />
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto">
                <table className="table-shell m-4 w-[calc(100%-2rem)] min-w-[600px] text-left text-sm">
                  <thead className="table-head">
                    <tr>
                      <th className="px-3 py-2">Organização</th>
                      <th className="px-3 py-2">Plano</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Vencimento</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {billingSubs.map((sub) => (
                      <tr key={sub.id} className={`hover:bg-slate-50 transition-colors ${selectedBillingOrgId === sub.organization_id ? 'bg-primary-50' : ''}`}>
                        <td className="px-3 py-2 font-medium text-slate-900">{sub.organization_name || sub.organization_id.slice(0, 8)}</td>
                        <td className="px-3 py-2 text-slate-700">{sub.plan_code}</td>
                        <td className="px-3 py-2"><StatusBadge value={sub.status} /></td>
                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(sub.current_period_end)}</td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" className="btn-secondary py-1 px-2 text-xs" onClick={() => setSelectedBillingOrgId(sub.organization_id)}>
                            Detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Controle da assinatura</h2>
                <p className="panel-subtitle">Ative, suspenda, troque plano ou estenda prazos.</p>
              </div>
            </div>
            {!selectedBillingOrgId ? (
              <div className="p-4">
                <EmptyState icon={CreditCard} title="Selecione uma organização" description="Clique em 'Detalhes' ao lado de uma assinatura para gerenciá-la." />
              </div>
            ) : billingLoading && !billingOrgDetail ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : billingOrgDetail ? (
              <div className="space-y-4 p-4">
                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="text-sm font-semibold text-slate-700">{billingOrgDetail.organization.name || 'Sem nome'}</p>
                  <p className="text-xs text-slate-500">{billingOrgDetail.organization.id}</p>
                </div>

                {billingOrgDetail.subscription && (
                  <div className="rounded-lg border border-slate-200 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Status</p>
                      <StatusBadge value={String(billingOrgDetail.subscription.status || 'unknown')} />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Plano</p>
                      <p className="text-sm font-semibold">{String(billingOrgDetail.subscription.plan_code || '—')}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Início</p>
                      <p className="text-sm">{formatDate(String(billingOrgDetail.subscription.current_period_start || ''))}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Vencimento</p>
                      <p className="text-sm">{formatDate(String(billingOrgDetail.subscription.current_period_end || ''))}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Período (dias)</p>
                      <p className="text-sm">{String(billingOrgDetail.subscription.period_days || '—')}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Membros</p>
                      <p className="text-sm">{billingOrgDetail.members?.length || 0}</p>
                    </div>
                  </div>
                )}

                {/* Controls */}
                <div className="space-y-3 pt-2">
                  {/* Activate */}
                  <div className="rounded-lg border border-success-200 bg-success-50 p-3">
                    <p className="text-xs font-semibold uppercase text-success-800">Ativar manualmente</p>
                    <input className="input mt-2 text-sm" placeholder="Motivo (opcional)" value={activateNote} onChange={(e) => setActivateNote(e.target.value)} />
                    <button type="button" className="btn-success mt-2 w-full text-xs" disabled={saving} onClick={() => selectedBillingOrgId && handleActivateSubscription(selectedBillingOrgId)}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Ativar assinatura
                    </button>
                  </div>

                  {/* Deactivate */}
                  <div className="rounded-lg border border-danger-200 bg-danger-50 p-3">
                    <p className="text-xs font-semibold uppercase text-danger-800">Suspender / Bloquear</p>
                    <input className="input mt-2 text-sm" placeholder="Motivo (obrigatório)" value={deactivateNote} onChange={(e) => setDeactivateNote(e.target.value)} />
                    <button type="button" className="btn-danger mt-2 w-full text-xs" disabled={saving || !deactivateNote} onClick={() => selectedBillingOrgId && handleDeactivateSubscription(selectedBillingOrgId)}>
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Suspender
                    </button>
                  </div>

                  {/* Change plan */}
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-xs font-semibold uppercase text-slate-700">Trocar plano</p>
                    <select className="input mt-2 text-sm" value={changePlanCode} onChange={(e) => setChangePlanCode(e.target.value)}>
                      <option value="">Selecione...</option>
                      {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button type="button" className="btn-secondary mt-2 w-full text-xs" disabled={saving || !changePlanCode} onClick={() => selectedBillingOrgId && handleChangePlan(selectedBillingOrgId)}>
                      Alterar plano
                    </button>
                  </div>

                  {/* Extend */}
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-xs font-semibold uppercase text-slate-700">Estender período</p>
                    <div className="mt-2 flex items-center gap-2">
                      <input className="input flex-1 text-sm" type="number" min={1} value={extendDays} onChange={(e) => setExtendDays(Number(e.target.value))} />
                      <span className="text-xs text-slate-500">dias</span>
                    </div>
                    <button type="button" className="btn-secondary mt-2 w-full text-xs" disabled={saving || extendDays < 1} onClick={() => selectedBillingOrgId && handleExtendPeriod(selectedBillingOrgId)}>
                      Estender
                    </button>
                  </div>

                  {/* Override limits */}
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-xs font-semibold uppercase text-slate-700">Sobrescrever limites</p>
                    {!showLimitsForm ? (
                      <button type="button" className="btn-secondary mt-2 w-full text-xs" onClick={() => { setShowLimitsForm(true); setLimitOverrides({}) }}>
                        Editar limites do plano
                      </button>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {['users', 'phone_numbers', 'voice_minutes', 'sms_segments', 'ai_agents'].map((key) => (
                          <div key={key} className="flex items-center gap-2">
                            <label className="w-28 text-xs text-slate-600">{key}</label>
                            <input className="input flex-1 text-sm" type="number" min={0} placeholder="ilimitado" value={limitOverrides[key] ?? ''} onChange={(e) => setLimitOverrides((prev) => { const next = { ...prev }; if (e.target.value === '') { delete next[key]; } else { next[key] = Number(e.target.value); } return next; })} />
                          </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <button type="button" className="btn-primary flex-1 text-xs" disabled={saving} onClick={() => selectedBillingOrgId && handleOverrideLimits(selectedBillingOrgId)}>
                            Salvar limites
                          </button>
                          <button type="button" className="btn-secondary text-xs" onClick={() => { setShowLimitsForm(false); setLimitOverrides({}) }}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Manual logs */}
                {billingOrgDetail.manual_logs && billingOrgDetail.manual_logs.length > 0 && (
                  <div className="pt-2">
                    <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Histórico manual</p>
                    <div className="max-h-40 space-y-1 overflow-y-auto">
                      {billingOrgDetail.manual_logs.slice(0, 10).map((log: Record<string, unknown>) => (
                        <div key={String(log.id)} className="rounded bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
                          <span className="font-semibold">{String(log.action)}</span>
                          {' — '}{String(log.note || '')}
                          <span className="ml-2 text-slate-400">{formatDate(String(log.created_at || ''))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>
      )}

      {/* ── Impersonation tab ── */}
      {activeTab === 'impersonation' && (
        <div className="grid gap-6 xl:grid-cols-2">
          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Iniciar impersonação</h2>
                <p className="panel-subtitle">Acesso assistido com motivo e expiração de 30 minutos.</p>
              </div>
            </div>
            <div className="space-y-3 p-4">
              <select
                className="input"
                value={impersonationTarget}
                onChange={(e) => setImpersonationTarget(e.target.value)}
              >
                <option value="">Selecione o usuário alvo</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} — {user.email}
                  </option>
                ))}
              </select>
              <textarea
                className="input min-h-[80px] resize-y"
                placeholder="Motivo obrigatório (mínimo 8 caracteres)"
                value={impersonationReason}
                onChange={(e) => setImpersonationReason(e.target.value)}
              />
              <button
                type="button"
                className="btn-danger w-full"
                disabled={saving || !impersonationTarget || impersonationReason.length < 8}
                onClick={handleStartImpersonation}
              >
                <Eye className="h-4 w-4" />
                Iniciar Impersonação
              </button>
            </div>
          </section>

          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Sessões ativas</h2>
                <p className="panel-subtitle">Impersonações em andamento. Tudo é registrado na auditoria.</p>
              </div>
            </div>
            {activeImpersonations.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={Shield}
                  title="Nenhuma sessão ativa"
                  description="Não há impersonações em andamento no momento."
                />
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {activeImpersonations.map((session) => (
                  <div
                    key={session.id}
                    className="bg-warning-50 border border-warning-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-warning-600 shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-warning-950 truncate">
                          {session.target?.name || 'Usuário desconhecido'}
                        </p>
                        <p className="text-xs text-warning-800 truncate">{session.target?.email}</p>
                        <p className="mt-1 text-xs text-warning-700">
                          Motivo: <span className="font-medium">{session.reason}</span>
                        </p>
                        <p className="mt-1 text-xs text-warning-700 tabular-nums">
                          Expira em {formatDate(session.expires_at)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-danger mt-3"
                      onClick={() =>
                        runAction(() => apiService.endImpersonation(session.id), 'Impersonação encerrada.')
                      }
                    >
                      <StopCircle className="h-4 w-4" />
                      Encerrar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Audit tab ── */}
      {activeTab === 'audit' && (
        <section className="surface">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Log de auditoria</h2>
              <p className="panel-subtitle">Eventos administrativos, billing e impersonação registrados automaticamente.</p>
            </div>
          </div>
          {auditLogs.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={FileText}
                title="Nenhum evento registrado"
                description="Os logs de auditoria aparecerão aqui conforme ações forem executadas."
              />
            </div>
          ) : (
            <div className="table-shell m-4 overflow-x-auto">
              <table className="min-w-[760px] w-full text-left text-sm">
                <thead className="table-head">
                  <tr>
                    <th className="px-4 py-3">Timestamp</th>
                    <th className="px-4 py-3">Ator</th>
                    <th className="px-4 py-3">Ação</th>
                    <th className="px-4 py-3">Entidade</th>
                    <th className="px-4 py-3">Recurso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <span
                          className="text-xs font-mono text-slate-600 cursor-default"
                          title={formatDate(log.created_at)}
                        >
                          {formatRelative(log.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="text-sm font-mono text-slate-700 truncate max-w-[100px] inline-block"
                          title={log.actor_user_id}
                        >
                          {shortUuid(log.actor_user_id)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={auditActionBadgeClass(log.action)}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 truncate max-w-[120px]">{log.resource_type}</td>
                      <td className="px-4 py-3">
                        {log.resource_id ? (
                          <span
                            className="text-xs font-mono text-slate-500 cursor-default"
                            title={log.resource_id}
                          >
                            {shortUuid(log.resource_id)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── Platform config tab ── */}
      {activeTab === 'platform' && currentUser?.is_platform_admin && (
        <div className="space-y-6">
          <section className="surface">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Configurações da Plataforma</h2>
                <p className="panel-subtitle">
                  Credenciais de ElevenLabs, Evolution e Wavoip para todo o SaaS. Evolution é usado para provisionar WhatsApp por usuário.
                </p>
              </div>
            </div>
            <div className="grid gap-4 p-4 sm:grid-cols-2">
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">ElevenLabs API Key</span>
                <input
                  className="input"
                  type="password"
                  value={platformDraft.elevenlabs_api_key || ''}
                  placeholder={platformConfig.elevenlabs_api_key ? '••••••••' : 'Cole a API key'}
                  onChange={(e) => setPlatformDraft({ ...platformDraft, elevenlabs_api_key: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">ElevenLabs Base URL</span>
                <input
                  className="input"
                  type="url"
                  value={platformDraft.elevenlabs_base_url || ''}
                  placeholder="https://api.elevenlabs.io/v1"
                  onChange={(e) => setPlatformDraft({ ...platformDraft, elevenlabs_base_url: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wavoip Device Token</span>
                <input
                  className="input"
                  type="password"
                  value={platformDraft.wavoip_device_token || ''}
                  placeholder={platformConfig.wavoip_device_token ? '••••••••' : 'Cole o token do dispositivo'}
                  onChange={(e) => setPlatformDraft({ ...platformDraft, wavoip_device_token: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wavoip SIP Host</span>
                <input
                  className="input"
                  value={platformDraft.wavoip_sip_host || ''}
                  placeholder="sipv2.wavoip.com"
                  onChange={(e) => setPlatformDraft({ ...platformDraft, wavoip_sip_host: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wavoip Phone Number</span>
                <input
                  className="input"
                  value={platformDraft.wavoip_phone_number || ''}
                  placeholder="+5511999999999"
                  onChange={(e) => setPlatformDraft({ ...platformDraft, wavoip_phone_number: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Evolution URL</span>
                <input
                  className="input"
                  type="url"
                  value={platformDraft.evolution_url || ''}
                  placeholder="https://evolution.seudominio.com.br"
                  onChange={(e) => setPlatformDraft({ ...platformDraft, evolution_url: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Evolution API Key</span>
                <input
                  className="input"
                  type="password"
                  value={platformDraft.evolution_api_key || ''}
                  placeholder={platformConfig.evolution_api_key ? '••••••••' : 'Cole a API key da Evolution'}
                  onChange={(e) => setPlatformDraft({ ...platformDraft, evolution_api_key: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">AbacatePay API Key</span>
                <input
                  className="input"
                  type="password"
                  value={platformDraft.abacatepay_api_key || ''}
                  placeholder={platformConfig.abacatepay_api_key ? '••••••••' : 'Cole a API key'}
                  onChange={(e) => setPlatformDraft({ ...platformDraft, abacatepay_api_key: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">AbacatePay Base URL</span>
                <input
                  className="input"
                  type="url"
                  value={platformDraft.abacatepay_base_url || ''}
                  placeholder="https://api.abacatepay.com/v2"
                  onChange={(e) => setPlatformDraft({ ...platformDraft, abacatepay_base_url: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">AbacatePay Webhook Secret</span>
                <input
                  className="input"
                  type="password"
                  value={platformDraft.abacatepay_webhook_secret || ''}
                  placeholder={platformConfig.abacatepay_webhook_secret ? '••••••••' : 'Cole o webhook secret'}
                  onChange={(e) => setPlatformDraft({ ...platformDraft, abacatepay_webhook_secret: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">AbacatePay Return URL</span>
                <input
                  className="input"
                  type="url"
                  value={platformDraft.abacatepay_return_url || ''}
                  placeholder="http://localhost:5177/app/billing"
                  onChange={(e) => setPlatformDraft({ ...platformDraft, abacatepay_return_url: e.target.value })}
                />
              </label>
            </div>
            <div className="px-4 pb-4">
              <button
                type="button"
                className="btn-primary"
                disabled={platformSaving}
                onClick={handleSavePlatformConfig}
              >
                {platformSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar'}
              </button>
            </div>
          </section>

          {platformConfig.wavoip_device_token && (
            <section className="surface">
              <div className="panel-header">
                <div>
                  <h2 className="panel-title">Conexão Wavoip</h2>
                  <p className="panel-subtitle">Use o token abaixo para configurar o dispositivo.</p>
                </div>
              </div>
              <div className="p-4">
                <WavoipTokenCard token={platformConfig.wavoip_device_token} />
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
